/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty, isUndefined, sortBy } from 'lodash';
import { CAP_TABLE_TERMS, SHARES_FULLY_DILUTED_ALIAS } from 'common/constants/cap-table';
import CellAnnotationValueViewer from 'components/CellAnnotationValueViewer';
import { buildCellKey } from 'components/CellAnnotationValueViewer/types';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { useVirtualColumns } from 'pages/CapTable/cap-table/components/captable-table/hooks';
import conditions from 'pages/CapTable/cap-table/utilities/conditions';
import { customValidations, reverseParser } from 'pages/CapTable/cap-table/utilities/index';
import parser from 'pages/CapTable/cap-table/utilities/parser';
import { FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG } from 'pages/CapTable/cell-annotations';
import { useCapTableDirtyCellStore } from 'store/useCapTableDirtyCellStore';
import { CapTableCellWithReferences, CaptableColumn } from '../components/captable-table/CaptableTable.props';
import { rowConfig } from '../data';
import afterCellChanged from './afterCellChanged';
import { aiMetadataKeyOf } from './aiFieldMap';
import { mergeSecuritiesWithLocal } from './mergeSecuritiesWithLocal';
import totalParser from './totalParser';
import { UseCaptableDataProps, UseCapTableDataResponse } from './useCaptableData.types';
import { useCapTableRowGroups } from './useCapTableRowGroups';

const useCaptableData = ({
  captableInfo,
  fundList,
  existingHiddenNoteColumns,
  measurementDate,
  fieldAttributes,
  isDisabled,
  hasVariableLiquidationPreference,
  hasVariableDividendRate,
  firmName,
  captableCurrency,
  isExportMode = false,
}: UseCaptableDataProps): UseCapTableDataResponse => {
  // setColumns should only be used for changing the shape of the table (adding/removing) and initial sync from API data
  const [columns, setColumns] = useState<CaptableColumn[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<CaptableColumn[] | undefined>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>();
  const [spreadsheet, setSpreadsheet] = useState<SpreadsheetConfig | null | undefined>(undefined);
  const captableSetProgrammaticUpdate = useRef(false);

  const setCaptableSetProgrammaticUpdate = useCallback((isProgrammatic: boolean) => {
    captableSetProgrammaticUpdate.current = isProgrammatic;
  }, []);

  const syncColumnsFromSecurities = useCallback(() => {
    if (!captableInfo?.securities) {
      return;
    }
    const incoming = captableInfo.securities as CaptableColumn[];
    const { dirtyBySecurityId } = useCapTableDirtyCellStore.getState();
    setColumns(prev => {
      const merged = mergeSecuritiesWithLocal(incoming, prev, dirtyBySecurityId);
      let changed = merged !== prev;
      const next = merged.map(col => {
        if (col.columnRef || !col.security_ref) {
          return col;
        }
        changed = true;
        return { ...col, columnRef: col.security_ref };
      });
      return changed ? next : prev;
    });
  }, [captableInfo?.securities]);

  useEffect(() => {
    syncColumnsFromSecurities();
  }, [captableInfo?.securities]);

  const { virtualColumns } = useVirtualColumns({ columns, captableInfo });

  // Update the visible columns when a column is deleted or added
  // and adds the result of resolving virtual columns to the visible columns
  useEffect(() => {
    if (columns && measurementDate) {
      const tmpVisibleColumns = columns.filter(column => !column.is_deleted && !column.is_hidden);
      const visibleVirtualColumnRefs = new Set(
        tmpVisibleColumns.filter(({ isVirtualColumn }) => isVirtualColumn).map(({ security_ref }) => security_ref)
      );

      if (virtualColumns && !isEmpty(virtualColumns)) {
        virtualColumns.forEach(vc => {
          // Insert virtual columns
          if (!visibleVirtualColumnRefs.has(vc.security_ref)) {
            // eslint-disable-next-line no-param-reassign
            vc.isVirtualColumn = true;
            tmpVisibleColumns.push(vc);
          }
        });
      }
      setVisibleColumns(sortBy(tmpVisibleColumns, ['order']));
    } else {
      setVisibleColumns(undefined);
    }
  }, [columns, virtualColumns, measurementDate, hasVariableLiquidationPreference]);

  // Omit fund-ownership rows from the cap-table PDF export so the captured table
  // isn't shrunk to fit them. The same details are exported separately on the
  // Fund Ownership page (FUND_OWNERSHIP target). rowConfig no-ops when this is
  // empty/undefined, dropping all three fund row groups and their divider.
  const fundOwnershipDetails = useMemo(
    () => (isExportMode ? undefined : captableInfo?.fund_ownership?.fund_ownership_detail),
    [captableInfo?.fund_ownership, isExportMode]
  );

  const config = useMemo(
    () => rowConfig({ measurementDate, isDisabled, fundOwnershipDetails, fundList, firmName }),
    [measurementDate, isDisabled, fundOwnershipDetails, fundList]
  );

  // Use custom hook for row groups persistence
  const capTableId = captableInfo?.id;
  const { rowGroups, setRowGroups } = useCapTableRowGroups(capTableId);

  const handleCellChanged = useCallback((initialChanges: any, cells: any, rowConfigParam: any, tableDataParam: any) => {
    // Skip clearing logic if this is a programmatic update
    if (!captableSetProgrammaticUpdate.current) {
      initialChanges.forEach((change: { cell: CapTableCellWithReferences; value: any }) => {
        if (!change.cell) {
          return;
        }
        // Unsaved new columns have a falsy columnId and are intentionally skipped:
        // the merge preserves unsaved local columns wholesale. SCAL-8959.
        const { columnId, alias } = change.cell;
        if (columnId && alias) {
          // ICell widens columnId to string | number, but cap-table cells always
          // carry the numeric security id (parser sets columnId: column.id).
          useCapTableDirtyCellStore.getState().recordDirty(columnId as number, alias, change.value);
        }
        const shouldRemoveMappingRefs =
          ((change.cell as CapTableCellWithReferences).mappingReferences?.length || 0) > 0 &&
          +change.value !== +change.cell.value &&
          +change.cell.value !== 0;
        const shouldRemoveAiMetadata = Boolean(change.cell.aiFieldMetadata);

        if (shouldRemoveMappingRefs || shouldRemoveAiMetadata) {
          // A manual edit strips the mapping/AI decoration, but must never
          // leave a select cell without its base viewer: SelectValueViewer is
          // the only component mapping a stored ref to its display name (e.g.
          // Underlying Security), and nothing downstream restores it. Fall
          // back to the row's base viewer (null when the row defines none).
          change.cell.className = '';
          change.cell.valueViewer = rowConfigParam?.find((row: any) => row.alias === alias)?.valueViewer ?? null;
        }

        if (shouldRemoveMappingRefs) {
          // Clear the cell's mapping references (only on manual edits)
          change.cell.mappingReferences = [];
          change.cell.columnarReferences = [];
          change.cell.columnarReferencesMetadata = [];
          change.cell.dispatchEvent?.();
        }

        if (shouldRemoveAiMetadata) {
          change.cell.aiFieldMetadata = null;
          change.cell.citationReferences = [];
          if (columnId && alias) {
            const fieldMetadata = tableDataParam?.columns?.find((c: any) => c.id === columnId)?.ai_finding
              ?.field_metadata;
            if (fieldMetadata) {
              // Findings are keyed by the canonical model field name (the
              // parser attaches them via aiFieldMap); legacy rows may also
              // carry a raw alias twin - purge both spellings.
              delete fieldMetadata[aiMetadataKeyOf(alias)];
              delete fieldMetadata[alias];
            }
          }
        }
      });
    }

    return afterCellChanged(
      initialChanges,
      cells,
      rowConfigParam,
      tableDataParam,
      captableSetProgrammaticUpdate.current
    );
  }, []);

  useEffect(() => {
    if (captableInfo?.securities?.length) {
      syncColumnsFromSecurities();
      setIsButtonDisabled(false);
    }
  }, [captableInfo]);

  const totalParserWithCellAnnotation = useMemo(() => {
    const captableId = captableInfo?.id;
    if (captableId == null) {
      return totalParser;
    }

    // The annotation_type segment ('cross_reference') matches the wire shape
    // returned by the backend for this cell - see useHydrateCaptableCellAnnotations.
    const annotationCellKey = buildCellKey('captable', captableId, 'fully_diluted_total', 'cross_reference');

    return (args: Parameters<typeof totalParser>[0]) => {
      const totals = totalParser(args);
      Object.values(totals).forEach(cell => {
        if (cell.alias === SHARES_FULLY_DILUTED_ALIAS) {
          cell.valueViewer = CellAnnotationValueViewer;
          cell.cellAnnotation = FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG;
          cell.cellAnnotationCellKey = annotationCellKey;
        }
      });
      return totals;
    };
  }, [captableInfo?.id]);

  useEffect(() => {
    if (!isUndefined(visibleColumns) && existingHiddenNoteColumns) {
      setSpreadsheet(
        new SpreadsheetConfig({
          columns: visibleColumns || [],
          parser,
          totalParser: totalParserWithCellAnnotation,
          rowConfig: config,
          conditions: conditions as any,
          name: 'captable',
          tableData: {
            columns,
            ...captableInfo,
            has_liquidation_preference_ledger: hasVariableLiquidationPreference,
            has_dividend_rate_ledger: hasVariableDividendRate,
            measurementDate,
            captableCurrency,
            isDisabled,
            existingHiddenNoteColumns,
          },
          // any as the constructor types these as `undefined`...
          reverseParser: reverseParser as any,
          afterCellChanged: handleCellChanged as any,
          customValidations: customValidations as any,
          showTotalColumn: true,
          showToolbar: true,
          tableTerms: CAP_TABLE_TERMS as any,
          allowConfirmAndDeleteColumn: !isDisabled,
          customDeleteConfirmation: !isDisabled,
          hasColTitle: true,
          currencyFormatter: false,
          allowEmptyValues: false,
          allowReorderColumns: !isDisabled,
          fieldAttributes,
          allowDeleteColumn: false,
          allowCopyRows: !isDisabled,
          allowCloneColumn: !isDisabled as any,
          allowAddMultipleColumns: !isDisabled,
          displayRowIndicator: true,
          displayColumnIndicator: true,
          // added during ts migration
          format: undefined,
          unitsFormatter: undefined,
        })
      );
    } else {
      setSpreadsheet(null);
    }
  }, [
    visibleColumns,
    isDisabled,
    hasVariableLiquidationPreference,
    hasVariableDividendRate,
    existingHiddenNoteColumns,
    totalParserWithCellAnnotation,
    captableCurrency,
    measurementDate,
  ]);

  return {
    spreadsheet,
    rowGroups,
    setRowGroups,
    tableTerms: CAP_TABLE_TERMS,
    totalParser,
    visibleColumns: visibleColumns ?? [],
    setVisibleColumns,
    virtualColumns,
    setColumns,
    columns,
    isButtonDisabled: isButtonDisabled ?? false,
    setIsButtonDisabled,
    setCaptableSetProgrammaticUpdate,
  };
};

export default useCaptableData;
